import { type Action, createActionsHook, createHook, createStore } from 'react-sweet-state';

import { type UserSearchItem } from '@atlaskit/smart-common';
import { type AutoSuggestionResult } from '@atlassian/search-client';

import { type SearchLinkProps } from '../../../common/ui/search-link/types';
import { type SearchResultCount } from '../../../common/utils/events/dropdown-results-shown';

type PeopleAndTeamsResults = {
	results: UserSearchItem[];
	duration: number;
};

type AutoSuggestionsResults = {
	results: AutoSuggestionResult[];
	duration: number;
};

export type SelectedInputFilter = {
	id: string;
	displayText: string;
};

export type DropdownState = {
	showDropdown: boolean;
	dropdownLoading: boolean;
	activeQueryFilterId: string;
	prefetchedRecentViewsResults: SearchLinkProps[] | undefined;
	prefetchedRecentSearchResults: SearchLinkProps[];
	peopleAndTeamsResults: PeopleAndTeamsResults;
	recentViewsResults: SearchLinkProps[];
	autoSuggestionResults: AutoSuggestionsResults;
	recentSearchResults: SearchLinkProps[];
	resultCount: SearchResultCount;
	selectedInputFilters: SelectedInputFilter[];
};

/**
 * This state object stores the results generated/displayed for each result type.
 */
export const initialState = {
	showDropdown: false,
	dropdownLoading: true,
	activeQueryFilterId: '',
	prefetchedRecentViewsResults: undefined,
	prefetchedRecentSearchResults: [],
	peopleAndTeamsResults: {
		results: [],
		duration: -1,
	},
	recentViewsResults: [],
	autoSuggestionResults: {
		results: [],
		duration: -1,
	},
	recentSearchResults: [],
	resultCount: {
		peopleAndTeams: 0,
		autoSuggestion: 0,
		recentSearch: 0,
		recentViews: 0,
	},
	selectedInputFilters: [],
};

const actions = {
	setShowDropdown:
		(showDropdown: boolean): Action<DropdownState> =>
		({ setState }) => {
			setState({ showDropdown });
		},
	setDropdownLoading:
		(dropdownLoading: boolean): Action<DropdownState> =>
		({ setState }) => {
			setState({ dropdownLoading });
		},
	setActiveQueryFilterId:
		(activeQueryFilterId: string): Action<DropdownState> =>
		({ setState }) => {
			setState({ activeQueryFilterId });
		},
	setPeopleAndTeamsResults:
		(peopleAndTeamsResults: PeopleAndTeamsResults): Action<DropdownState> =>
		({ setState }) => {
			setState({ peopleAndTeamsResults });
		},
	setRecentViewsResults:
		(recentViewsResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ recentViewsResults });
		},
	setAutoSuggestionResults:
		(autoSuggestionResults: AutoSuggestionsResults): Action<DropdownState> =>
		({ setState }) => {
			setState({ autoSuggestionResults });
		},
	setRecentSearchResults:
		(recentSearchResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ recentSearchResults });
		},
	setResultCount:
		(resultCount: SearchResultCount): Action<DropdownState> =>
		({ setState }) => {
			setState({ resultCount });
		},
	setPrefetchedRecentViewsResults:
		(prefetchedRecentViewsResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ prefetchedRecentViewsResults });
		},
	setPrefetchedRecentSearchResults:
		(prefetchedRecentSearchResults: SearchLinkProps[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ prefetchedRecentSearchResults });
		},
	setSelectedInputFilters:
		(selectedInputFilters: SelectedInputFilter[]): Action<DropdownState> =>
		({ setState }) => {
			setState({ selectedInputFilters });
		},
};

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<DropdownState, TActions>({
	initialState,
	actions,
	name: 'dropdown',
});

// Hooks

export const useDropdownActions = createActionsHook(Store);

export const useDropdown = createHook(Store, {
	selector: (state) => state.showDropdown,
});

export const useDropdownLoading = createHook(Store, {
	selector: (state) => state.dropdownLoading,
});

export const useActiveQueryFilterId = createHook(Store, {
	selector: (state) => state.activeQueryFilterId,
});

export const usePeopleAndTeamsResults = createHook(Store, {
	selector: (state) => state.peopleAndTeamsResults,
});

export const useRecentViewsResults = createHook(Store, {
	selector: (state) => state.recentViewsResults,
});

export const useAutoSuggestionResults = createHook(Store, {
	selector: (state) => state.autoSuggestionResults,
});

export const useRecentSearchResults = createHook(Store, {
	selector: (state) => state.recentSearchResults,
});

export const useResultCount = createHook(Store, {
	selector: (state) => state.resultCount,
});

export const usePrefetchedRecentViewsResults = createHook(Store, {
	selector: (state) => state.prefetchedRecentViewsResults,
});

export const usePrefetchedRecentSearchResults = createHook(Store, {
	selector: (state) => state.prefetchedRecentSearchResults,
});

export const useSelectedInputFilters = createHook(Store, {
	selector: (state) => state.selectedInputFilters,
});
